import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Container,
  Paper,
  Typography,
  Table, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableCell, 
  Button, 
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Pagination,
  SelectChangeEvent,
  TableContainer,
  useMediaQuery,
  useTheme,
  Grid,
  IconButton,
  Badge,
  Tooltip,
  Menu,
  MenuItem as MuiMenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Input,
  Switch
} from '@mui/material';
import { 
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  MoreVert as MoreVertIcon,
  Upload as UploadIcon,
  FileUpload as FileUploadIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import userService, { User, UserFilter } from '../../services/userService';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { AuthUtils } from '../../utils/AuthUtils';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import config from '../../config';
import { UserRole } from '../../types/user';
import { getStaffAvailability } from '../../services/shift';
// import UserTable from '../../features/UserTable'; // UserTableをインポート

// ユーザー一覧ページで使用する拡張ユーザー型
interface ExtendedUser extends User {
  // hasActiveShift?: boolean; // 現在シフト中かどうかのフラグを削除
}

const USER_ROLE_LABELS: Record<string, string> = {
  'ADMIN': '管理者',
  'MANAGER': 'マネジャー',
  'STAFF': '内勤スタッフ',
  'DRIVER': 'ドライバー',
  'CAST': 'キャスト',
  'CUSTOMER': '顧客'
};

const USER_ROLE_COLORS: Record<string, "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning"> = {
  'ADMIN': 'error',
  'MANAGER': 'warning',
  'STAFF': 'info',
  'DRIVER': 'success',
  'CAST': 'primary',
  'CUSTOMER': 'secondary'
};

// 役割の正規化関数（大文字に変換）
const normalizeRole = (role: string): string => {
  if (!role) return '';
  return role.toUpperCase();
};

// 役割名と色のマッピングを修正して、大文字小文字を区別しないようにする
const getRoleLabel = (role: string): string => {
  const normalizedRole = normalizeRole(role);
  return USER_ROLE_LABELS[normalizedRole as keyof typeof USER_ROLE_LABELS] || normalizedRole;
};

const getRoleColor = (role: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
  const normalizedRole = normalizeRole(role);
  return USER_ROLE_COLORS[normalizedRole as keyof typeof USER_ROLE_COLORS] || 'default';
};

const UserListPage: React.FC = () => {
  const [users, setUsers] = useState<ExtendedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalUsers, setTotalUsers] = useState(0);
  const [filter, setFilter] = useState<{
    role?: string;
    status?: string;
  }>({
    role: undefined,
    status: undefined
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [actionAnchorEl, setActionAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [activeShifts, setActiveShifts] = useState<any[]>([]);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();

  const handleToggleStatus = async (userId: number) => {
    try {
      const updatedUser = await userService.toggleUserStatus(userId);
      setUsers(currentUsers =>
        currentUsers.map(user =>
          user.id === userId ? { ...user, ...updatedUser } : user
        )
      );
      enqueueSnackbar('ユーザーステータスが更新されました', { variant: 'success' });
    } catch (err: any) {
      console.error('ステータスの更新に失敗しました:', err);
      enqueueSnackbar(err.message || 'ステータスの更新に失敗しました', { variant: 'error' });
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      // APIリクエスト用のフィルターを作成
      const filterParams: UserFilter = {};
      
      // ロールのフィルタリング - 空文字でない場合のみ追加
      if (filter.role && filter.role !== '') {
        filterParams.role = filter.role;
        console.log(`${filter.role}ロールのユーザーをフィルタリング`);
      }

      // ステータスのフィルタリング - 空文字でない場合のみ追加
      if (filter.status && filter.status !== '') {
        filterParams.status = filter.status;
        console.log(`${filter.status}ステータスのユーザーをフィルタリング`);
      }
      
      // 検索キーワードのフィルタリング
      if (searchTerm && searchTerm.trim() !== '') {
        filterParams.search = searchTerm.trim();
        console.log(`"${searchTerm}"でユーザーを検索`);
      }

      console.log('送信するフィルターパラメータ:', filterParams);

      // ユーザー情報取得
      const response = await userService.getUsers(
        page, 
        pageSize, 
        filterParams
      );

      console.log('フェッチしたユーザー:', response);
      console.log('取得したユーザー数:', response.users.length);
      
      // 各ユーザーのデータを詳細にログ
      response.users.forEach((user, index) => {
        console.log(`ユーザー[${index}]:`, user);
        console.log(`  - ID: ${user.id}, 名前: ${user.first_name} ${user.last_name}`);
        console.log(`  - 電話番号: phone_number=${user.phone_number}`);
      });

      // シフト情報と組み合わせた拡張ユーザー情報を作成
      const extendedUsers = response.users.map(user => ({
        ...user,
        // hasActiveShift: activeShiftUsers[user.id.toString()] || false
      }));

      setUsers(extendedUsers);
      setTotalUsers(response.total);
      setLoading(false);
    } catch (err) {
      console.error('ユーザー一覧の取得エラー:', err);
      const errorMessage = err instanceof Error ? err.message : '予期せぬエラーが発生しました';
      setError(errorMessage);
      setLoading(false);
    }
  };

  useEffect(() => {
    // シフト情報取得部分を削除し、直接ユーザーを取得する
    fetchUsers();

    // 現在ログインしているユーザー情報を取得
    const loggedInUser = AuthUtils.getStoredUser();
    if (loggedInUser) {
      // AuthUtilsのUserとuserServiceのUser型に互換性を持たせるために変換
      const userWithStatus = {
        ...loggedInUser,
        status: 'active' // デフォルトでアクティブとして扱う
      };
      setCurrentUser(userWithStatus as User);
    }
  }, [page, filter, searchTerm]);

  const handleCreateUser = () => {
    navigate('/users/create');
  };

  const handleEditUser = (userId: number) => {
    navigate(`/users/edit/${userId}`);
  };

  const handleDeleteUser = async (user: User) => {
    try {
      await userService.deleteUser(user.id);
      setUsers(users.filter(u => u.id !== user.id));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '予期せぬエラーが発生しました';
      setError(errorMessage);
    }
  };

  const handleRoleChange = (event: SelectChangeEvent<string>) => {
    const role = event.target.value;
    setPage(1); // フィルタ変更時は1ページ目に戻す
    setFilter(prev => ({
      ...prev,
      role: role === '' ? undefined : role
    }));
  };

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    const status = event.target.value;
    setPage(1); // フィルタ変更時は1ページ目に戻す
    setFilter(prev => ({
      ...prev,
      status: status === '' ? undefined : status
    }));
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  // 検索・絞り込みはすべてサーバーサイド(fetchUsers)で処理するため、
  // ここではサーバーから取得したユーザーをそのまま表示する。
  const filteredUsers = users;

  // アクションメニューのオプションを定義
  const actionOptions = [
    { label: '編集', value: 'edit' }
  ];

  // アクションメニューのレンダリング部分を修正
  const handleActionClick = (event: React.MouseEvent<HTMLButtonElement>, user: User) => {
    setActionAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleActionClose = () => {
    setActionAnchorEl(null);
    setSelectedUser(null);
  };

  const handleActionSelect = (value: string, user: User) => {
    if (value === 'edit') {
      handleEditUser(user.id);
    }
  };

  // ユーザーが管理者かどうかを確認
  const isAdmin = useMemo(() => {
    return currentUser?.role?.toUpperCase() === 'ADMIN';
  }, [currentUser]);

  // CSVファイル選択ハンドラー
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setCsvFile(event.target.files[0]);
    }
  };

  // CSVインポートダイアログを開く
  const handleOpenImportDialog = () => {
    setImportDialogOpen(true);
  };

  // CSVインポートダイアログを閉じる
  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
    setCsvFile(null);
  };

  const handleImportCsv = async () => {
    if (!csvFile) {
      enqueueSnackbar('CSVファイルを選択してください', { variant: 'warning' });
      return;
    }

    setImporting(true);
    setError(null);

    try {
      const response = await userService.importCsv(csvFile);
      enqueueSnackbar(response.message || 'ユーザーのインポートに成功しました', { variant: 'success' });
      handleCloseImportDialog();
      fetchUsers(); // ユーザーリストを再取得
    } catch (err: any) {
        console.error('CSVインポートエラー:', err);
        
        let errorMessage = 'ユーザーのインポートに失敗しました。';
        
        // Axiosエラーの場合、レスポンスから詳細なエラーメッセージを取得
        if (axios.isAxiosError(err) && err.response) {
            console.error('バックエンドからのエラーレスポンス:', err.response.data);
            const errorData = err.response.data;
            if (errorData.error) {
                // 'error' と 'details' を組み合わせる
                errorMessage = errorData.error;
                if (errorData.details && Array.isArray(errorData.details)) {
                    errorMessage += `: ${errorData.details.join(', ')}`;
                } else if (errorData.details) {
                    errorMessage += `: ${errorData.details}`;
                }
            } else if (errorData.message) {
                errorMessage = errorData.message;
            }
        } else if (err instanceof Error) {
            errorMessage = err.message;
        }
        
        console.error('表示する最終的なエラーメッセージ:', errorMessage);
        setError(errorMessage); // アラートとして表示
        enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 10000 }); // スナックバーでも表示
    } finally {
      setImporting(false);
    }
  };

  // CSVファイルをエクスポート
  const handleExportCsv = async () => {
    try {
      setExporting(true);
      const blob = await userService.exportCsv();
      
      // ファイル名を生成（現在の日時を含む）
      const now = new Date();
      const dateStr = format(now, 'yyyyMMdd_HHmmss');
      const fileName = `users_export_${dateStr}.csv`;
      
      // ダウンロードリンクを作成
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      
      // リンクをクリックしてダウンロード
      document.body.appendChild(link);
      link.click();
      
      // クリーンアップ
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      enqueueSnackbar('CSVエクスポートが完了しました', { variant: 'success' });
    } catch (error) {
      console.error('CSVエクスポートに失敗しました:', error);
      enqueueSnackbar('CSVエクスポートに失敗しました', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  /*
  const renderUsers = () => {
    if (loading) return <CircularProgress />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (users.length === 0) return <Typography>ユーザーが見つかりません。</Typography>;

    return <UserTable users={users} />;
  };
  */

  return (
    <DashboardLayout title="ユーザー管理">
    <Container maxWidth="lg">
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h1" fontWeight="bold">
              ユーザー管理
            </Typography>
          </Box>
          
          {/* レスポンシブ対応したフィルター部分 */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={8} md={9}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={4}>
            <TextField 
                    placeholder="名前・メール・電話番号で検索" 
              value={searchTerm}
              onChange={(e) => { setPage(1); setSearchTerm(e.target.value); }}
                    fullWidth
                    size={isMobile ? "small" : "medium"}
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1 }} />
              }}
            />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Box sx={{ display: 'flex', gap: 1, mt: { xs: 1, sm: 0 } }}>
                    {isAdmin && (
                      <>
                        <Button
                          variant="outlined"
                          color="primary"
                          startIcon={<UploadIcon />}
                          onClick={handleOpenImportDialog}
                          size={isMobile ? "small" : "medium"}
                        >
                          CSV取込
                        </Button>
                        <Button
                          variant="outlined"
                          color="secondary"
                          startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
                          onClick={handleExportCsv}
                          disabled={exporting || loading}
                          size={isMobile ? "small" : "medium"}
                        >
                          CSVエクスポート
                        </Button>
                      </>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <FormControl fullWidth size={isMobile ? "small" : "medium"}>
              <InputLabel>役割</InputLabel>
              <Select 
                value={filter.role || ''}
                onChange={handleRoleChange}
                label="役割"
              >
                <MenuItem value="">すべての役割</MenuItem>
                      <MenuItem value={UserRole.ADMIN}>管理者</MenuItem>
                      <MenuItem value={UserRole.MANAGER}>マネジャー</MenuItem>
                      <MenuItem value={UserRole.DISPATCHER}>内勤スタッフ</MenuItem>
                      <MenuItem value={UserRole.DRIVER}>ドライバー</MenuItem>
                      <MenuItem value={UserRole.CAST}>キャスト</MenuItem>
                      <MenuItem value={UserRole.CUSTOMER}>顧客</MenuItem>
              </Select>
            </FormControl>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <FormControl fullWidth size={isMobile ? "small" : "medium"}>
              <InputLabel>ステータス</InputLabel>
              <Select 
                value={filter.status || ''}
                onChange={handleStatusChange}
                label="ステータス"
              >
                      <MenuItem value="">すべて</MenuItem>
                      <MenuItem value="active">有効</MenuItem>
                      <MenuItem value="inactive">無効</MenuItem>
              </Select>
            </FormControl>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={4} md={3} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, gap: 1 }}>
          <Button 
            variant="contained" 
            color="primary" 
                startIcon={!isMobile && <AddIcon />}
            onClick={handleCreateUser}
                fullWidth={isMobile}
                size={isMobile ? "small" : "medium"}
          >
                {isMobile ? "新規作成" : "新規ユーザー作成"}
          </Button>
            </Grid>
          </Grid>

          {/* スクロール可能なテーブル */}
        <Paper elevation={3}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size={isMobile ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                    {!isMobile && <TableCell>ID</TableCell>}
                    <TableCell>名前</TableCell>
                    {!isMobile && <TableCell>電話番号</TableCell>}
                <TableCell>役割</TableCell>
                <TableCell>ステータス</TableCell>
                    <TableCell align="right">アクション</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map(user => (
                <TableRow key={user.id}>
                        {!isMobile && <TableCell>{user.id}</TableCell>}
                  <TableCell>
                          {user.first_name || user.username}
                          {isMobile && <Box mt={0.5} sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{user.phone_number || '-'}</Box>}
                  </TableCell>
                        {!isMobile && <TableCell>{user.phone_number || '-'}</TableCell>}
                  <TableCell>
                    <Chip 
                            label={getRoleLabel(user.role)}
                            color={getRoleColor(user.role)}
                            size={isMobile ? "small" : "medium"}
                    />
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={user.is_active}
                      onChange={() => handleToggleStatus(user.id)}
                      color="primary"
                    />
                  </TableCell>
                        <TableCell align="right">
                      <Button 
                            variant="contained"
                            color="primary"
                        size="small" 
                        onClick={() => handleEditUser(user.id)}
                      >
                        編集
                      </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={isMobile ? 4 : 5} align="center">
                        ユーザーが見つかりません
                  </TableCell>
                </TableRow>
                  )}
            </TableBody>
          </Table>
            </TableContainer>
        </Paper>

        {/* {renderUsers()} */}

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={Math.ceil(totalUsers / pageSize)}
            page={page}
            onChange={handlePageChange}
            color="primary"
              size={isMobile ? "small" : "medium"}
          />
        </Box>
      </Box>
    </Container>
      
      {/* CSVインポートダイアログ */}
      {/* renderUsers() */}
    </DashboardLayout>
  );
};

export default UserListPage;
